import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Stack, Card, CardContent, Button, TextField,
  CircularProgress, Alert, Grid, Chip, IconButton, Tooltip, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  ArrowBack, Add, Delete, Straighten, TrendingUp, TrendingDown,
  AccessibilityNew, FitnessCenter, DirectionsWalk, DirectionsRun,
  SportsGymnastics, RadioButtonUnchecked, AirlineSeatLegroomNormal, MonitorWeight,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { analyticsAPI, usersAPI } from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';

// Measurement fields — icons instead of emojis (cross-browser safe)
const FIELDS = [
  { key: 'weight',     label: 'Weight (kg)',  Icon: MonitorWeight,             color: '#0284C7' },
  { key: 'bodyFatPercentage', label: 'Fat %', Icon: TrendingDown,              color: '#DC2626' },
  { key: 'muscleMass', label: 'Muscle (kg)',  Icon: AccessibilityNew,          color: '#65A30D' },
  { key: 'chest',      label: 'Chest',        Icon: RadioButtonUnchecked,      color: '#1a1a2ed4' },
  { key: 'waist',      label: 'Waist',        Icon: Straighten,                color: '#FF6B35' },
  { key: 'hips',       label: 'Hips',         Icon: AirlineSeatLegroomNormal,  color: '#FF4757' },
  { key: 'leftArm',    label: 'Left Arm',     Icon: FitnessCenter,             color: '#2ED573' },
  { key: 'rightArm',   label: 'Right Arm',    Icon: FitnessCenter,             color: '#1E90FF' },
  { key: 'leftThigh',  label: 'Left Thigh',   Icon: DirectionsWalk,            color: '#A29BFE' },
  { key: 'rightThigh', label: 'Right Thigh',  Icon: DirectionsWalk,            color: '#FF9F43' },
  { key: 'shoulders',  label: 'Shoulders',    Icon: SportsGymnastics,          color: '#26C6DA' },
  { key: 'neck',       label: 'Neck',         Icon: RadioButtonUnchecked,      color: '#4CAF82' },
  { key: 'calves',     label: 'Calves',       Icon: DirectionsRun,             color: '#FF6B81' },
];

const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', {
  day: 'numeric', month: 'short', year: 'numeric',
});

// Mini sparkline (SVG)
function Sparkline({ values, color }) {
  if (!values || values.length < 2) return null;
  const w = 80, h = 30, pad = 4;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function BodyMeasurements() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [formNotes, setFormNotes] = useState('');
  const [formDate, setFormDate] = useState(new Date().toISOString().slice(0, 10));

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [userRes, measRes] = await Promise.all([
        usersAPI.getOne(userId),
        analyticsAPI.getUserMeasurements(userId),
      ]);
      setUser(userRes.data.data);
      setLogs(measRes.data.data || []);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load measurements');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    const hasAny = FIELDS.some(f => form[f.key] && form[f.key] !== '');
    if (!hasAny) return;
    setSaving(true);
    try {
      const payload = { date: formDate, notes: formNotes };
      FIELDS.forEach(f => {
        payload[f.key] = form[f.key] ? parseFloat(form[f.key]) : null;
      });
      await analyticsAPI.addUserMeasurement(userId, payload);
      setAddOpen(false);
      setForm({});
      setFormNotes('');
      setFormDate(new Date().toISOString().slice(0, 10));
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleteConfirm({ open: true, id });
  };

  const doDelete = async () => {
    try {
      await analyticsAPI.deleteUserMeasurement(deleteConfirm.id);
      await load();
    } catch (e) {
      setError('Failed to delete');
    } finally {
      setDeleteConfirm({ open: false, id: null });
    }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
      <CircularProgress />
    </Box>
  );

  const sorted = [...logs].sort((a, b) => new Date(a.date) - new Date(b.date));

  const fieldTrends = FIELDS.map(f => {
    const vals = sorted.filter(l => l[f.key] != null).map(l => ({ date: l.date, value: l[f.key] }));
    const first = vals[0]?.value;
    const last  = vals[vals.length - 1]?.value;
    const diff  = vals.length >= 2 ? (last - first).toFixed(1) : null;
    return { ...f, vals, first, last, diff };
  }).filter(f => f.vals.length > 0);

  return (
    <Box p={3}>
      {/* Header */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2.5, overflow: 'hidden' }}>
        <Box sx={{ height: 3, background: 'linear-gradient(90deg, #7C3AED, #A78BFA)' }} />
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => navigate(-1)} size="small" sx={{ flexShrink: 0 }}>
              <ArrowBack fontSize="small" />
            </IconButton>
            <Avatar sx={{
              bgcolor: 'primary.main',
              width: 48, height: 48,
              fontSize: 20, fontWeight: 700,
              flexShrink: 0,
              boxShadow: '0 0 0 3px rgba(124,58,237,0.15)',
            }}>
              {user?.name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography variant="h6" fontWeight={700} noWrap>{user?.name}</Typography>
                <Chip label="Body Measurements" size="small" color="primary" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" noWrap>
                {user?.email}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add sx={{ fontSize: '14px !important' }} />}
              onClick={() => setAddOpen(true)}
              sx={{ flexShrink: 0 }}
            >
              Add Entry
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Trend summary cards */}
      {fieldTrends.length > 0 && (
        <Grid container spacing={2} mb={3}>
          {fieldTrends.map(f => {
            const FieldIcon = f.Icon;
            return (
              <Grid item xs={6} sm={4} md={3} key={f.key}>
                <Card variant="outlined" sx={{ borderRadius: 2.5, border: `1px solid ${f.color}22` }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <FieldIcon sx={{ fontSize: 14, color: f.color }} />
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          {f.label}
                        </Typography>
                      </Stack>
                      {f.diff !== null && (
                        <Chip
                          icon={parseFloat(f.diff) >= 0
                            ? <TrendingUp sx={{ fontSize: 12 }} />
                            : <TrendingDown sx={{ fontSize: 12 }} />}
                          label={`${parseFloat(f.diff) >= 0 ? '+' : ''}${f.diff} cm`}
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700,
                            bgcolor: `${f.color}18`, color: f.color }}
                        />
                      )}
                    </Stack>
                    <Typography variant="h6" fontWeight={800} sx={{ color: f.color, fontSize: '1.1rem' }}>
                      {f.last?.toFixed(1)} cm
                    </Typography>
                    <Typography variant="caption" color="text.disabled">
                      {f.vals.length} entries
                    </Typography>
                    <Box mt={0.5}>
                      <Sparkline values={f.vals.map(v => v.value)} color={f.color} />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* History table */}
      <Card variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <Box sx={{ height: 3, background: 'linear-gradient(90deg, #1a1a2ed4, #A29BFE)' }} />
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} mb={2}>
            Measurement History ({logs.length} entries)
          </Typography>
          {logs.length === 0 ? (
            <Box display="flex" flexDirection="column" alignItems="center" py={6}>
              <Straighten sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No measurements recorded yet</Typography>
              <Button variant="outlined" startIcon={<Add />} sx={{ mt: 2 }} onClick={() => setAddOpen(true)}>
                Add First Entry
              </Button>
            </Box>
          ) : (
            <TableContainer sx={{ borderRadius: 1.5, border: 1, borderColor: 'divider', maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Date</TableCell>
                    {FIELDS.map(f => {
                      const FieldIcon = f.Icon;
                      return (
                        <TableCell key={f.key} align="right"
                          sx={{ fontWeight: 700, fontSize: '0.7rem', color: f.color }}>
                          <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.3}>
                            <FieldIcon sx={{ fontSize: 12 }} />
                            <span>{f.label}</span>
                          </Stack>
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Added By</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Notes</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...logs].sort((a, b) => new Date(b.date) - new Date(a.date)).map((log, i) => (
                    <TableRow key={i} hover sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {fmtDate(log.date)}
                      </TableCell>
                      {FIELDS.map(f => (
                        <TableCell key={f.key} align="right" sx={{ fontSize: '0.8rem' }}>
                          {log[f.key] != null ? (
                            <Typography variant="caption" fontWeight={600} sx={{ color: f.color }}>
                              {log[f.key].toFixed(1)}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Chip
                            label={log.addedByName || log.addedBy || 'user'}
                            size="small"
                            sx={{
                              height: 20, fontSize: '0.65rem',
                              bgcolor: log.addedBy === 'user' ? '#1a1a2ed418' : '#FF6B3518',
                              color: log.addedBy === 'user' ? '#1a1a2ed4' : '#FF6B35',
                              fontWeight: 600,
                            }}
                          />
                          {log.source === 'inbody' && (
                            <Chip
                              icon={<MonitorWeight sx={{ fontSize: 10 }} />}
                              label="InBody Sync"
                              size="small"
                              sx={{
                                height: 18, fontSize: '0.6rem',
                                bgcolor: '#0284C718',
                                color: '#0284C7',
                                fontWeight: 700,
                              }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary', maxWidth: 120 }}>
                        {log.notes || '—'}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => handleDelete(log._id)} color="error">
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Add dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Straighten sx={{ color: '#1a1a2ed4' }} />
            <Typography fontWeight={700}>Add Measurements for {user?.name}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField
              label="Date"
              type="date"
              value={formDate}
              onChange={e => setFormDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
            />
            <Grid container spacing={1.5}>
              {FIELDS.map(f => {
                const FieldIcon = f.Icon;
                return (
                  <Grid item xs={6} key={f.key}>
                    <TextField
                      label={f.label + ' (cm)'}
                      type="number"
                      inputProps={{ step: '0.1', min: '0' }}
                      value={form[f.key] || ''}
                      onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                      size="small"
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <FieldIcon sx={{ fontSize: 16, color: f.color, mr: 0.5 }} />
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-focused fieldset': { borderColor: f.color },
                        },
                        '& label.Mui-focused': { color: f.color },
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
            <TextField
              label="Notes (optional)"
              value={formNotes}
              onChange={e => setFormNotes(e.target.value)}
              multiline rows={2}
              size="small"
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={saving || !FIELDS.some(f => form[f.key])}
            sx={{ bgcolor: '#1a1a2ed4', '&:hover': { bgcolor: '#5850E6' } }}
          >
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Save Entry'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteConfirm.open}
        title="Delete Measurement"
        message="This will permanently delete this measurement entry."
        confirmLabel="Delete"
        onConfirm={doDelete}
        onCancel={() => setDeleteConfirm({ open: false, id: null })}
      />
    </Box>
  );
}
